import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

export default function ParticleNetwork() {
  const svgRef = useRef(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    let width = window.innerWidth;
    let height = window.innerHeight * 3;
    const numParticles = 80;
    const connectionDistance = 150;
    const mouse = { x: -1000, y: -1000 };

    svg.attr('width', width).attr('height', height);

    const particles = d3.range(numParticles).map(() => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 3 + 1.5,
    }));

    const lineGroup = svg.append('g');
    const circleGroup = svg.append('g');

    const circles = circleGroup
      .selectAll('circle')
      .data(particles)
      .enter()
      .append('circle')
      .attr('r', (d) => d.r)
      .attr('fill', (d, i) => {
        const colors = ['#818cf8', '#22d3ee', '#a78bfa', '#6366f1'];
        return colors[i % colors.length];
      })
      .attr('opacity', 0.5);

    function handleMouseMove(e) {
      mouse.x = e.clientX;
      mouse.y = e.clientY + window.scrollY;
    }

    window.addEventListener('mousemove', handleMouseMove);

    function tick() {
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;

        const dx = mouse.x - p.x;
        const dy = mouse.y - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 200) {
          p.vx += dx * 0.00005;
          p.vy += dy * 0.00005;
        }

        p.vx *= 0.999;
        p.vy *= 0.999;
      });

      circles.attr('cx', (d) => d.x).attr('cy', (d) => d.y);

      const lines = [];
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < connectionDistance) {
            lines.push({ source: particles[i], target: particles[j], dist });
          }
        }
      }

      const lineSelection = lineGroup.selectAll('line').data(lines);
      lineSelection.exit().remove();
      lineSelection
        .enter()
        .append('line')
        .merge(lineSelection)
        .attr('x1', (d) => d.source.x)
        .attr('y1', (d) => d.source.y)
        .attr('x2', (d) => d.target.x)
        .attr('y2', (d) => d.target.y)
        .attr('stroke', '#818cf8')
        .attr('stroke-opacity', (d) => 0.15 * (1 - d.dist / connectionDistance))
        .attr('stroke-width', 0.5);

      requestAnimationFrame(tick);
    }

    tick();

    function handleResize() {
      width = window.innerWidth;
      height = document.documentElement.scrollHeight;
      svg.attr('width', width).attr('height', height);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <svg
      ref={svgRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none z-0"
      style={{ position: 'fixed' }}
    />
  );
}
